/**
 * GrapesEmailEditor — drag-and-drop email builder using GrapesJS + newsletter preset.
 * Outputs email-safe HTML (table-based layout, inline styles).
 *
 * Swappable with Unlayer: npm install @unlayer/react, replace with UnlayerEditor wrapper
 * calling editor.exportHtml() on change. Interface (value/onChange) is identical.
 */

import React, { useEffect, useRef, useState } from "react";

const VELARO_VARS = [
  { label: "First Name",  token: "<<contact.firstName>>" },
  { label: "Last Name",   token: "<<contact.lastName>>" },
  { label: "Email",       token: "<<contact.email>>" },
  { label: "Company",     token: "<<contact.company>>" },
  { label: "Unsubscribe", token: "{{unsubscribe_url}}" },
];

interface Props {
  value: string;
  onChange: (html: string) => void;
  uploadUrl?: string;
}

export default function GrapesEmailEditor({ value, onChange, uploadUrl }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const editorRef    = useRef<any>(null);
  const [preview, setPreview]       = useState<"desktop" | "mobile">("desktop");
  const [showPreview, setShowPreview] = useState(false);
  const [previewHtml, setPreviewHtml] = useState("");
  const [darkPreview, setDarkPreview] = useState(false);
  const [inited, setInited]         = useState(false);
  const [varOpen, setVarOpen]       = useState(false);

  useEffect(() => {
    if (!containerRef.current || inited) return;
    setInited(true);

    Promise.all([
      import("grapesjs"),
      import("grapesjs-preset-newsletter"),
    ]).then(([gjs, preset]) => {
      const editor = gjs.default.init({
        container: containerRef.current!,
        height: "100%",
        width: "100%",
        storageManager: false,
        plugins: [preset.default],
        pluginsOpts: {
          [preset.default as any]: {
            modalLabelImport: "Paste your HTML here",
            modalLabelExport: "Export HTML",
            codeViewerTheme: "hopscotch",
            imageEditor: false,
          },
        },
        assetManager: uploadUrl ? { upload: uploadUrl, uploadName: "file" } : undefined,
        components: value || defaultTemplate,
        style: "",
      });

      editorRef.current = editor;
      editor.on("update", () => onChange(editor.getHtml()));

      VELARO_VARS.forEach(v => {
        editor.BlockManager.add(`velaro-var-${v.token}`, {
          label: v.label,
          category: "Velaro Variables",
          content: `<span style="font-family:inherit">${v.token}</span>`,
          attributes: { class: "gjs-block-section" },
        });
      });
    });

    return () => { editorRef.current?.destroy?.(); editorRef.current = null; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const openPreview = () => {
    if (editorRef.current) setPreviewHtml(editorRef.current.getHtml());
    setShowPreview(true);
  };

  const insertVar = (token: string) => {
    if (editorRef.current) {
      const sel = editorRef.current.getSelected();
      if (sel) sel.set("content", (sel.get("content") || "") + token);
    }
    setVarOpen(false);
  };

  return (
    <div className="flex flex-col flex-1 min-h-0 rounded-xl border border-slate-200 overflow-hidden bg-white shadow-sm">
      {/* ── Toolbar ── */}
      <div className="flex items-center gap-2 px-4 py-2.5 bg-white border-b border-slate-100 shrink-0">
        {/* Label */}
        <span className="text-xs font-semibold text-slate-400 uppercase tracking-widest select-none">Design</span>
        <div className="flex-1" />

        {/* Variables */}
        <div className="relative">
          <button
            type="button"
            onClick={() => setVarOpen(v => !v)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-slate-200 bg-slate-50 text-slate-600 hover:bg-slate-100 hover:text-slate-800 transition-colors"
          >
            <svg className="w-3.5 h-3.5" viewBox="0 0 16 16" fill="none">
              <path d="M2 4h3M11 4h3M5.5 4C5.5 5.5 4 6.5 4 8s1.5 2.5 1.5 4M10.5 4C10.5 5.5 12 6.5 12 8s-1.5 2.5-1.5 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
            Variables
          </button>
          {varOpen && (
            <div className="absolute top-full right-0 mt-1.5 bg-white border border-slate-200 rounded-xl shadow-lg z-20 min-w-[190px] py-1 overflow-hidden">
              <p className="px-3 py-1.5 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Insert token</p>
              {VELARO_VARS.map(v => (
                <button key={v.token} type="button" onClick={() => insertVar(v.token)}
                  className="flex items-center justify-between w-full px-3 py-2 text-xs hover:bg-slate-50 transition-colors">
                  <span className="text-slate-700 font-medium">{v.label}</span>
                  <code className="text-[10px] text-slate-400 font-mono">{v.token}</code>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Preview */}
        <button type="button" onClick={openPreview}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-slate-200 bg-slate-50 text-slate-600 hover:bg-slate-100 hover:text-slate-800 transition-colors">
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
          </svg>
          Preview
        </button>
      </div>

      {/* ── GrapesJS canvas ── */}
      <div ref={containerRef} className="flex-1 min-h-0" />

      {/* ── Preview modal ── */}
      {showPreview && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl flex flex-col" style={{ width: "92vw", maxWidth: 940, maxHeight: "92vh" }}>

            {/* Modal header */}
            <div className="flex items-center gap-3 px-6 py-4 border-b border-slate-100">
              <div>
                <p className="text-sm font-semibold text-slate-800">Email Preview</p>
                <p className="text-xs text-slate-400 mt-0.5">How recipients will see this email</p>
              </div>
              <div className="flex-1" />

              {/* Device toggle — pill style */}
              <div className="flex items-center bg-slate-100 rounded-lg p-0.5 gap-0.5">
                {(["desktop", "mobile"] as const).map(d => (
                  <button key={d} type="button" onClick={() => setPreview(d)}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
                      preview === d ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-700"
                    }`}
                  >
                    {d === "desktop"
                      ? <><svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8M12 17v4"/></svg>Desktop</>
                      : <><svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><rect x="5" y="2" width="14" height="20" rx="2"/><circle cx="12" cy="18" r="1" fill="currentColor"/></svg>Mobile</>
                    }
                  </button>
                ))}
              </div>

              {/* Dark mode */}
              <button type="button" onClick={() => setDarkPreview(v => !v)}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border transition-all ${
                  darkPreview ? "bg-slate-900 text-white border-slate-700" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                }`}
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                </svg>
                Dark
              </button>

              <button type="button" onClick={() => setShowPreview(false)}
                className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors text-lg font-light ml-1">
                ×
              </button>
            </div>

            {/* Inbox simulation bar */}
            <div className="px-6 py-3 bg-slate-50 border-b border-slate-100 flex items-center gap-3">
              <div className="w-9 h-9 bg-primary rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0">Y</div>
              <div className="min-w-0 flex-1">
                <p className="text-xs font-semibold text-slate-800 truncate">From: Your Company &lt;hello@yourdomain.com&gt;</p>
                <p className="text-xs text-slate-400 truncate mt-0.5">Subject line · Preview text shows here — first ~90 chars of body</p>
              </div>
              <span className="text-[10px] text-slate-400 flex-shrink-0 bg-slate-200 px-2 py-0.5 rounded-full font-medium">
                {preview === "mobile" ? "375px" : "680px"}
              </span>
            </div>

            {/* Preview canvas */}
            <div className={`flex-1 overflow-auto flex justify-center items-start p-8 transition-colors ${darkPreview ? "bg-slate-900" : "bg-slate-100"}`}>
              <div style={{
                width: preview === "mobile" ? 375 : "100%",
                maxWidth: preview === "desktop" ? 680 : 375,
                transition: "all 0.25s cubic-bezier(.4,0,.2,1)",
                boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
                borderRadius: 12,
                overflow: "hidden",
                background: darkPreview ? "#1e1e1e" : "#fff",
                filter: darkPreview ? "invert(1) hue-rotate(180deg)" : "none",
              }}>
                <div dangerouslySetInnerHTML={{ __html: previewHtml || "<p style='padding:48px;color:#94a3b8;text-align:center;font-family:sans-serif'>No content yet — add blocks to the left.</p>" }} />
              </div>
            </div>

            {/* Footer hint */}
            <div className="px-6 py-3 border-t border-slate-100 flex items-center gap-2 text-xs text-slate-400">
              <svg className="w-3.5 h-3.5 text-amber-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd"/>
              </svg>
              Dark mode inverts colors to simulate email client dark rendering. Test on real devices before sending.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const defaultTemplate = `
<table width="100%" cellpadding="0" cellspacing="0" style="background:#f1f5f9;padding:48px 20px">
  <tr><td align="center">
    <table width="600" cellpadding="0" cellspacing="0" style="background:#ffffff;border-radius:12px;overflow:hidden;box-shadow:0 4px 24px rgba(0,0,0,0.06)">
      <tr><td style="background:#1670e7;padding:36px 48px;text-align:center">
        <p style="color:#ffffff;font-size:22px;font-weight:700;margin:0;font-family:Arial,sans-serif;letter-spacing:-0.3px">Your Company</p>
      </td></tr>
      <tr><td style="padding:48px">
        <p style="font-size:24px;font-weight:700;color:#0f172a;margin:0 0 12px;font-family:Arial,sans-serif;letter-spacing:-0.5px">Hi <<contact.firstName>>,</p>
        <p style="font-size:16px;color:#475569;line-height:1.7;margin:0 0 32px;font-family:Arial,sans-serif">
          We have something exciting to share with you. Click the button below to learn more.
        </p>
        <table cellpadding="0" cellspacing="0">
          <tr><td style="background:#1670e7;border-radius:8px;padding:14px 32px">
            <a href="#" style="color:#ffffff;font-size:15px;font-weight:600;text-decoration:none;font-family:Arial,sans-serif;letter-spacing:0.1px">Get Started →</a>
          </td></tr>
        </table>
        <p style="font-size:14px;color:#94a3b8;margin:40px 0 0;font-family:Arial,sans-serif">
          Questions? Reply to this email anytime.
        </p>
      </td></tr>
      <tr><td style="background:#f8fafc;padding:28px 48px;border-top:1px solid #e2e8f0">
        <p style="font-size:12px;color:#94a3b8;margin:0 0 6px;font-family:Arial,sans-serif;text-align:center">
          You received this because you signed up at ourwebsite.com
        </p>
        <p style="font-size:12px;color:#94a3b8;margin:0;font-family:Arial,sans-serif;text-align:center">
          <a href="{{unsubscribe_url}}" style="color:#94a3b8;text-decoration:underline">Unsubscribe</a> · 123 Business St, Your City
        </p>
      </td></tr>
    </table>
  </td></tr>
</table>`;
